import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectedCompanyData } from '../../lib/types/company.types';
import { logout } from './authSlice';

interface ProductServiceObj {
  value: string;
  exclude?: boolean;
}
interface LocationObj {
  name: string;
  exclude?: boolean;
  id?: string;
  /**
   * The same place name as `name`, kept under the keys the payload builders
   * read (`id: value || label || id`). See lib/utils/location-selection.ts.
   */
  label?: string;
  value?: string;
  /** Dropdown Service code - ISO country code, ISO 3166-2 state code, city id. */
  code?: string;
  /** Parent code: country code for a state, state code for a city. */
  parent?: string | null;
  countryId?: number;
  iso3?: string;
  iso2?: string;
  phoneCode?: string;
  capital?: string;
  currency?: string;
  region?: string;
  title?: string | null;
  isRegion?: boolean;
}
export interface PeopleFiltersState {
  selectedCountries: LocationObj[];
  searchState: LocationObj[];
  searchCity: LocationObj[];
  selectedSeniorityLevels: { value: string; exclude?: boolean }[];
  selectedFunctionalLevels: { value: string; exclude?: boolean }[];
  searchTitle: { value: string; exclude?: boolean }[];
  personName: string[];
  linkedInLink: any[];
  companyNameWebsite: string;
  industryType: string;
  industryKeyword: string[];
  selectedIndustries: { industry: string; exclude: boolean; id?: string; group?: string }[];
  // UK-only, complement to selectedIndustries: stays empty until the United
  // Kingdom is picked in the Location country filter (see PeopleFilters).
  selectedSubIndustries: { industry: string; exclude: boolean; id?: string; group?: string }[];
  selectedCompanySizes: { size: string; exclude: boolean; }[];
  selectedRevenue: { revenue: string; exclude?: boolean }[];
  companyLocation: string;
  productService: ProductServiceObj[];
  technologyParameter: { id: string; group: string; name: string; exclude: boolean; }[];
  suppressions: string;
  excludeViewedPeople: string;
  // --- Added for saved contacts filtering ---
  excludeExportedPeople: boolean;
  directDialOptions: {
    emailable: boolean;
    phoneable: boolean;
  };
  companyName: SelectedCompanyData[];
  websiteList: string[];
  // --- Added for company-style location filtering ---
  // LocationObj, not string: the payload builder reads `.value` / `.label` off
  // each entry (see buildPeopleSearchPayload), and the location filter has
  // always stored objects here - the string[] this used to claim was wrong.
  hqCountry: LocationObj[];
  hqState: LocationObj[];
  hqCity: LocationObj[];
  includeOffices: boolean;
  // --- Added for people suppression filtering ---
  personNameSuppressionList: string;
  personNameSuppressFromSearch: boolean;
  emailSuppressionList: string;
  emailSuppressFromSearch: boolean;
  websiteSuppressionList: string;
  websiteSuppressFromSearch: boolean;
  websiteShowSingleCompany: boolean;
  // --- Added for email confidence filtering ---
  isVerified: boolean;
  isGuessed: boolean;
  isNotFound: boolean;
  High: boolean;
  Medium: boolean;
  Low: boolean;
  ExportedPeopleStart?: string | null;
  ExportedPeopleEnd?: string | null;
  UnlockDateStart?: string | null;
  UnlockDateEnd?: string | null;
  // Company founding year range (inclusive), both set or both null
  foundedMinYear: number | null;
  foundedMaxYear: number | null;
}

const initialPeopleFilters: PeopleFiltersState = {
  selectedCountries: [],
  searchState: [],
  searchCity: [],
  selectedSeniorityLevels: [],
  selectedFunctionalLevels: [],
  searchTitle: [],
  personName: [],
  linkedInLink: [],
  companyNameWebsite: '',
  industryType: '',
  industryKeyword: [],
  selectedIndustries: [],
  selectedSubIndustries: [],
  selectedCompanySizes: [],
  selectedRevenue: [],
  companyLocation: '',
  productService: [],
  technologyParameter: [],
  suppressions: '',
  excludeViewedPeople: '',
  // --- Added for saved contacts filtering ---
  excludeExportedPeople: false,
  directDialOptions: {
    emailable: false,
    phoneable: false,
  },
  companyName: [],
  websiteList: [],
  // --- Added for company-style location filtering ---
  hqCountry: [],
  hqState: [],
  hqCity: [],
  includeOffices: false, // Disabled (and off) initially until a location is selected
  // --- Added for people suppression filtering ---
  personNameSuppressionList: '',
  personNameSuppressFromSearch: false,
  emailSuppressionList: '',
  emailSuppressFromSearch: false,
  websiteSuppressionList: '',
  websiteSuppressFromSearch: false,
  websiteShowSingleCompany: false,
  // --- Added for email confidence filtering ---
  isVerified: false,
  isGuessed: false,
  isNotFound: false,
  High: false,
  Medium: false,
  Low: false,
  ExportedPeopleStart: null,
  ExportedPeopleEnd: null,
  UnlockDateStart: null,
  UnlockDateEnd: null,
  foundedMinYear: null,
  foundedMaxYear: null,
};

/**
 * Only peopleFilters lives here. Search results, pagination and row selection are
 * owned by RTK Query (peopleApi) and by local component state - nothing reads
 * them back off this slice.
 */
const initialState: { peopleFilters: PeopleFiltersState } = {
  peopleFilters: initialPeopleFilters,
};

const peopleSlice = createSlice({
  name: 'people',
  initialState,
  reducers: {
    setPeopleFilterField: <K extends keyof PeopleFiltersState>(
      state: typeof initialState,
      action: PayloadAction<{
        field: K;
        value: PeopleFiltersState[K];
      }>
    ) => {
      state.peopleFilters[action.payload.field] = action.payload.value as any;
    },
    resetPeopleFilters: (state: typeof initialState) => {
      state.peopleFilters = { ...initialPeopleFilters };
    },
    updatePeopleFilters: (state: typeof initialState, action: PayloadAction<Partial<PeopleFiltersState>>) => {
      state.peopleFilters = { ...state.peopleFilters, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    // Client-side routing means the Redux store survives a logout - login
    // cycle (unlike a full page refresh, which cleanSession()'s localStorage
    // clear alone already covers). Without this, People search filters from
    // the previous session stayed in memory and reappeared for whoever logs
    // in next.
    builder.addCase(logout.fulfilled, () => ({ ...initialState }));
  },
});

export const {
  setPeopleFilterField,
  resetPeopleFilters,
  updatePeopleFilters,
} = peopleSlice.actions;

export default peopleSlice.reducer;
